import { effectiveEnergy, type Energy } from '@hmawiw/shared';
import { useNow } from '../../hooks/useMedia';
import { useT } from '../../i18n';
import { startOfDay } from '../../lib/dates';
import { boardCards, useCards, useBoardCards } from '../../store/cards';
import { useEvents } from '../../store/events';
import { useSettings } from '../../store/settings';
import controls from '../controls.module.css';
import { EnergyGlyph } from '../icons';
import { Sheet } from '../Sheet/Sheet';
import styles from './Levers.module.css';

const LEVELS: Energy[] = [1, 2, 3];

/** The morning check-in: your energy, then one dreaded thing for the day. Opened by the check-in notification. */
export function CheckinSheet({ open, onClose }: { open: boolean; onClose(): void }) {
  const t = useT();
  const now = useNow();
  const energyNow = useSettings((s) => s.energyNow);
  const set = useSettings((s) => s.set);
  const cards = useBoardCards();
  const log = useEvents((s) => s.log);
  const level = effectiveEnergy(energyNow, now) ?? 0;
  const dreading = boardCards(cards, now)
    .filter((c) => c.motivation === 0)
    .sort((a, b) => a.band - b.band || a.rank - b.rank)
    .slice(0, 8);
  const pick = (cardId: string | null) => {
    if (cardId) {
      void set('pinnedToday', { cardId, day: startOfDay(now) });
      void log('offer', cardId, { kind: 'morning', answer: 'pinned' });
    } else void log('offer', null, { kind: 'morning', answer: 'none' });
    onClose();
  };
  return (
    <Sheet open={open} label={t.t('checkin.title')} onClose={onClose}>
      <div className={controls.group}>
        <div className={controls.label}>{t.t('checkin.energy')}</div>
        <div className={controls.chips} role="group" aria-label={t.t('checkin.energy')}>
          {LEVELS.map((l) => (
            <button key={l} type="button" className={controls.chip} aria-pressed={level === l} onClick={() => void set('energyNow', { level: l, setAt: Date.now() })}>
              <EnergyGlyph level={l} size={18} />
              {t.t(`energy.${l}`)}
            </button>
          ))}
        </div>
      </div>
      <div className={controls.group}>
        <div className={controls.label}>{t.t('checkin.pick')}</div>
        {dreading.length === 0 ? <p className={styles.hint}>{t.t('checkin.none')}</p> : null}
        <div className={styles.list}>
          {dreading.map((c) => (
            <button key={c.id} type="button" className={styles.pickRow} onClick={() => pick(c.id)}>
              <span className={styles.pickTitle}>{c.title}</span>
              <span className={styles.pickMeta}>{t.band(c.band)}</span>
            </button>
          ))}
        </div>
      </div>
      <div className={controls.footer}>
        <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={() => pick(null)}>
          {t.t('checkin.notToday')}
        </button>
      </div>
    </Sheet>
  );
}
